import { Language } from '@yleisradio/areena-types';
import { Device, MediaLanguageCode } from '../appTypings';
import { logError } from '../error/errorUtils';
import { isoDurationToSeconds } from '../utils/duration';
import {
  getCustomViewIdentifier,
  uiLanguageToSnowplowLanguage,
} from './snowplowUtils';
import { getPackageId, getViewId } from '../utils/view';
import { getItemId } from '../Item/utils';
import { hbbTvMode, Mode, ps4Mode, ps5Mode } from '../../common/types';
import { addGlobalContexts } from '@snowplow/browser-tracker';

const seriesContextGenerator = (
  seriesId: string,
  predicate: string
): AnalyticsContext => ({
  schema: 'iglu:fi.yle.content/series/jsonschema/2-0-0',
  data: {
    series_id: seriesId,
    predicate: predicate,
  },
});

const programContextGenerator = (
  programId: string,
  predicate: string
): AnalyticsContext => ({
  schema: 'iglu:fi.yle.content/program/jsonschema/2-0-1',
  data: {
    program_id: programId,
    predicate: predicate,
  },
});

const seasonContextGenerator = (
  seasonId: string,
  predicate: string
): AnalyticsContext => ({
  schema: 'iglu:fi.yle.content/season/jsonschema/2-0-0',
  data: {
    season_id: seasonId,
    predicate: predicate,
  },
});

const serviceContextGenerator = (service_id: string) => ({
  schema: 'iglu:fi.yle.content/service/jsonschema/2-0-1',
  data: {
    service_id,
    predicate: 'media_playing',
  },
});

const liveMediaContextGenerator = (dvrWindow: string): AnalyticsContext => ({
  schema: 'iglu:fi.yle.player/live_stream/jsonschema/2-0-0',
  data: { dvr_window: isoDurationToSeconds(dvrWindow) },
});

const mediaContextGenerator = (media_id: string) => ({
  schema: 'iglu:fi.yle.content/media/jsonschema/2-1-1',
  data: {
    media_id,
    predicate: 'media_playing',
  },
});

export type ContextServiceLanguage = 'fin' | 'swe';
export type ContextContentLanguage = MediaLanguageCode | 'und' | 'mul';

type AnalyticsContextParams = {
  section: string | undefined;
  service_language: ContextServiceLanguage;
  content_language: ContextContentLanguage;
  context_index: number;
};

export const yleContextGenerator = ({
  section,
  service_language,
  content_language,
  context_index,
}: AnalyticsContextParams) => ({
  schema: 'iglu:fi.yle/yle_context/jsonschema/2-14-0',
  data: {
    main_service: 'yle-areena',
    service: 'areena-leanback',
    section: section || 'page',
    service_language,
    content_language,
    context_index,
    wrapper_type: 'web',
  },
});

const publicationContextGenerator = (publication_id: string) => ({
  schema: 'iglu:fi.yle.content/publication/jsonschema/2-0-0',
  data: {
    publication_id,
    predicate: 'media_playing',
  },
});

type MediaTrackingContextGeneratorParams = {
  section: string | undefined;
  service_language: Language;
  content_language: ContextContentLanguage;
  isLiveMedia: boolean;
  dvr_window: string | undefined;
  programId: string;
  service_id: string | undefined;
  media_id: string;
  publication_id: string | undefined;
};

export type AnalyticsContext = {
  schema: string;
  data: Record<string, unknown>;
};

export const mediaTrackingGenerator = ({
  isLiveMedia,
  dvr_window,
  programId,
  section,
  service_language,
  content_language,
  service_id,
  media_id,
  publication_id,
}: MediaTrackingContextGeneratorParams): AnalyticsContext[] => {
  const context: AnalyticsContext[] = [];

  const yleContext = yleContextGenerator({
    section: section || 'page',
    service_language: uiLanguageToSnowplowLanguage(service_language),
    content_language,
    context_index: 1,
  });

  // update global context instead of context.push, to avoid double yle contexts
  addGlobalContexts({
    yleContext,
  });

  if (isLiveMedia && dvr_window) {
    try {
      context.push(liveMediaContextGenerator(dvr_window));
    } catch (error) {
      logError(error);
    }
  }

  if (service_id) {
    context.push(serviceContextGenerator(service_id));
  }

  context.push(programContextGenerator(programId, 'media_playing'));

  if (media_id) {
    context.push(mediaContextGenerator(media_id));
  }
  if (publication_id) {
    context.push(publicationContextGenerator(publication_id));
  }

  return context;
};

type ActiveEntityContextGeneratorParams = {
  areena_api_yle_id?: string;
  areena_api_view_type: string | undefined;
  areena_api_entity_type: string | undefined;
  areenaApiUri: string | undefined;
  version: string | undefined;
};

export const activeEntityContextGenerator = ({
  areena_api_yle_id,
  areena_api_view_type,
  areenaApiUri,
  version,
  areena_api_entity_type,
}: ActiveEntityContextGeneratorParams) => {
  const areena_custom_view_identifier = getCustomViewIdentifier(areenaApiUri);

  if (areena_custom_view_identifier) {
    return customViewContextGenerator(areena_custom_view_identifier);
  }
  if (
    !areena_api_yle_id ||
    areena_api_view_type === 'player-page' ||
    areena_api_view_type === 'similars-page'
  )
    return undefined;

  const areena_view_id = getViewId(areena_api_yle_id);
  const areena_package_id = getPackageId(areena_api_yle_id);
  const areena_item_id = getItemId(areena_api_yle_id);

  if (areena_view_id) {
    return areenaViewContextGenerator(areena_view_id, version);
  }
  if (areena_item_id) {
    const predicate = 'consumed_activity';

    if (
      areena_api_entity_type === 'series' ||
      areena_api_view_type === 'series-page'
    ) {
      return seriesContextGenerator(areena_item_id, predicate);
    }
    if (
      areena_api_entity_type === 'program' ||
      areena_api_view_type === 'program-page'
    ) {
      return programContextGenerator(areena_item_id, predicate);
    }

    if (
      areena_api_entity_type === 'season' ||
      areena_api_view_type === 'season-page'
    ) {
      return seasonContextGenerator(areena_item_id, predicate);
    }
  }
  if (areena_package_id) {
    return areenaPackageContextGenerator({
      areena_package_id,
      predicate: 'consumed_activity',
    });
  }
};

const areenaViewContextGenerator = (
  areena_view_id: string,
  version: string | undefined
) => {
  const versionNumber = parseInt(version || '');
  return {
    schema: 'iglu:fi.yle.content/areena_view/jsonschema/2-1-0',
    data: {
      areena_view_id,
      version: isNaN(versionNumber) ? null : versionNumber,
      predicate: 'consumed_activity',
    },
  };
};

const customViewContextGenerator = (areena_custom_view_identifier: string) => {
  return {
    schema: 'iglu:fi.yle.content/areena_custom_view/jsonschema/2-0-0',
    data: {
      predicate: 'consumed_activity',
      areena_custom_view_identifier,
    },
  };
};

type AreenaPackageContextGeneratorParams = {
  areena_package_id: string;
  predicate: 'consumed_activity' | 'engagement_view_source';
};

export const areenaPackageContextGenerator = ({
  areena_package_id,
  predicate,
}: AreenaPackageContextGeneratorParams) => {
  return {
    schema: 'iglu:fi.yle.content/areena_package/jsonschema/2-0-0',
    data: {
      areena_package_id,
      predicate,
    },
  };
};

type GroupContextParams = {
  group_type: string | null;
  group_num_items: number | null;
  source_ref: string | null;
  source_url: string | null;
  group_label: string | null;
};

export const groupContextGenerator = ({
  group_type,
  group_num_items,
  source_ref,
  source_url,
  group_label,
}: GroupContextParams) => ({
  schema: 'iglu:fi.yle.ui/group/jsonschema/2-4-0',
  data: {
    type: group_type,
    num_items: group_num_items,
    source_ref,
    source_url,
    label: group_label,
    is_paginated: false,
  },
});

export const wrapperContextGenerator = (
  type: string = 'main',
  horizontal: boolean,
  label?: string
) => ({
  schema: 'iglu:fi.yle.ui/wrapper/jsonschema/2-4-0',
  data: {
    type: type,
    horizontal,
    ...(label && { label }),
  },
});

export const tabContextGenerator = (title: string) => ({
  schema: 'iglu:fi.yle.ui/tab/jsonschema/2-0-0',
  data: {
    title,
  },
});

type UiElementContextGeratorParams = {
  type: string;
  layout_size: string | null;
  group_index: number;
  group_order_index: number;
  wrapper_index: number | null;
  title: string;
};

export const uiElementContextGenerator = ({
  type,
  layout_size,
  group_index,
  group_order_index,
  wrapper_index,
  title,
}: UiElementContextGeratorParams) => ({
  schema: 'iglu:fi.yle.ui/element/jsonschema/2-1-0',
  data: {
    type,
    layout_size,
    group_index,
    group_order_index,
    wrapper_index,
    title,
  },
});

export const imageContextGenerator = (image_id: string | null) => ({
  schema: 'iglu:fi.yle.content/image/jsonschema/2-1-0',
  data: {
    image_id,
    predicate: 'engagement_view_source',
  },
});

export const entityContextGenerator = (entity_id: string) => ({
  schema: 'iglu:fi.yle.content/entity/jsonschema/2-0-1',
  data: {
    entity_id,
    predicate: 'engagement_view_target',
  },
});

export const recommendationContextGenerator = (
  recommendation_id: string,
  method: string
) => ({
  schema: 'iglu:fi.yle.content/recommendation/jsonschema/2-5-0',
  data: {
    recommendation_id,
    method,
    predicate: 'engagement_view_source',
  },
});

export const abTestContextGenerator = (name: string, group: string) => ({
  schema: 'iglu:fi.yle.ab/test/jsonschema/2-1-0',
  data: {
    name,
    group,
  },
});

export const engagementViewEventGenerator = (
  view_id: string,
  view_mode: string
) => ({
  schema: 'iglu:fi.yle.engagement/view/jsonschema/2-1-0',
  data: {
    view_id,
    view_type: 'entity',
    view_entity_type: 'entity',
    view_mode: view_mode,
  },
});

type consentContextGeneratorParams = {
  version: string;
  development: boolean;
  personalization: boolean;
  embedded_social_media: boolean;
  targeting: boolean;
};

export const consentContextGenerator = ({
  version,
  development,
  personalization,
  embedded_social_media,
  targeting,
}: consentContextGeneratorParams) => ({
  schema: 'iglu:fi.yle/consent/jsonschema/2-0-1',
  data: {
    version,
    development,
    personalization,
    embedded_social_media,
    targeting,
  },
});

export const userProfileContextGenerator = (ageGroup: number | undefined) => ({
  schema: 'iglu:fi.yle.content/user_profile/jsonschema/2-2-0',
  data: {
    age_group: ageGroup ?? null,
    predicate: 'consumer',
  },
});

const getPlatformDetail = (mode: Mode) => {
  if (mode === hbbTvMode) {
    return 'hbbtv';
  }
  if (mode === ps4Mode || mode === ps5Mode) {
    return 'playstation';
  }
  return 'web-smart-tv';
};

export const deviceContextGenerator = (
  device: Device | undefined,
  mode: Mode
) => {
  const deviceOs = `${device?.os ? device.os + ' ' : ''}${device?.version || ''}`;
  return {
    schema: 'iglu:fi.yle/device/jsonschema/2-0-0',
    data: {
      platform_detail: getPlatformDetail(mode),
      brand: device?.vendor || null,
      model: device?.model || null,
      model_year: !!device?.year ? parseInt(device.year, 10) : null,
      operating_system: deviceOs || null,
      client: 'areena.leanback',
    },
  };
};
